import { useEffect, useRef } from "react";
import { useField, useFormikContext, FieldAttributes } from "formik";

export function useDocumentTitle(title: string | undefined) {
  useEffect(() => {
    if (title !== undefined) {
      document.title = title;
    }
  }, [title]);
}

export function useFieldProps(name: string) {
  const { getFieldProps } = useFormikContext<any>();
  return getFieldProps({ name });
}

export function useFieldWithStatus(name: string) {
  const [field, meta] = useField(name);
  const { status } = useFormikContext();
  return [field, { ...meta, status: status && status[name] }] as const;
}

export function useFieldWithInitialValue<V>(
  fieldArgs: string | FieldAttributes<any>,
  value?: V,
) {
  const { setFieldValue } = useFormikContext<any>();
  const [field, meta] = useField(fieldArgs);
  useEffect(() => {
    if (!field.value && value) {
      setFieldValue(field.name, value);
    }
  }, [field.name, field.value, setFieldValue, value]);
  return [field, meta] as const;
}

// https://reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily
export function useLazyRef<T>(f: () => T) {
  const ref = useRef<T | null>(null);
  if (!ref.current) {
    ref.current = f();
  }
  return ref.current;
}

export function useElement(parent: HTMLElement, f: () => HTMLElement) {
  const ref = useLazyRef(f);

  useEffect(() => {
    parent.appendChild(ref);
    return () => {
      parent.removeChild(ref);
    };
  }, [parent, ref]);

  return ref;
}
